/**
 * useErrorHandler Hook
 * React hook for error handling with notifications and field error extraction
 *
 * Features:
 * - handleError() - Show error notification
 * - handleSuccess() - Show success notification
 * - getFieldError() - Extract form field errors
 * - createError() - Create custom errors with error codes
 * - Error type checkers (isNetworkError, isAuthError, etc.)
 */

import { useCallback } from 'react';
import { App } from 'antd';
import type { ErrorCode, ErrorContext } from './error-service';
import { ErrorService, ErrorSeverity } from './error-service';
import i18n from '@/i18n/config';

/**
 * Options for error handler
 */
interface UseErrorHandlerOptions {
  showNotification?: boolean;
  onError?: (error: unknown) => void;
}

/**
 * useErrorHandler hook
 * Uses App.useApp() to get context-aware message/notification APIs
 */
export function useErrorHandler(options?: UseErrorHandlerOptions) {
  // Get context-aware message and notification APIs from App
  const { message, notification } = App.useApp();

  /**
   * Handle error with notification (uses context-aware API)
   */
  const handleError = useCallback(
    (
      error: unknown,
      customOptions?: UseErrorHandlerOptions & { title?: string; duration?: number }
    ) => {
      const finalOptions = { ...options, ...customOptions };

      // Call custom error handler if provided
      finalOptions.onError?.(error);

      // Show notification if enabled (default: true)
      if (finalOptions.showNotification !== false) {
        const errorInfo = ErrorService.parseError(error);
        const severity = ErrorService.getSeverity(errorInfo.code);

        // Use context-aware message/notification APIs
        if (severity === ErrorSeverity.CRITICAL || customOptions?.title) {
          notification.error({
            message: customOptions?.title || i18n.t('common:errors.errorTitle', 'Error'),
            description: errorInfo.message,
            duration: customOptions?.duration ?? (severity === ErrorSeverity.CRITICAL ? 0 : 5),
          });
        } else {
          message.error(errorInfo.message, customOptions?.duration ?? 5);
        }
      }

      // Log error in development
      if (import.meta.env.DEV) {
        console.error('[useErrorHandler]', error);
      }
    },
    [options, message, notification]
  );

  /**
   * Handle success with notification (uses context-aware API)
   */
  const handleSuccess = useCallback(
    (messageText: string, duration?: number) => {
      message.success(messageText, duration ?? 3);
    },
    [message]
  );

  /**
   * Handle warning with notification (uses context-aware API)
   */
  const handleWarning = useCallback(
    (messageText: string, duration?: number) => {
      message.warning(messageText, duration ?? 4);
    },
    [message]
  );

  /**
   * Handle info with notification (uses context-aware API)
   */
  const handleInfo = useCallback(
    (messageText: string, duration?: number) => {
      message.info(messageText, duration ?? 3);
    },
    [message]
  );

  /**
   * Get field-specific error from validation error
   */
  const getFieldError = useCallback((error: unknown, fieldName: string) => {
    return ErrorService.getFieldError(error, fieldName);
  }, []);

  /**
   * Create custom error with error code
   */
  const createError = useCallback((code: ErrorCode, context?: ErrorContext) => {
    return ErrorService.createError(code, context);
  }, []);

  /**
   * Execute async operation with error handling (uses context-aware API)
   */
  const withErrorHandling = useCallback(
    async <T,>(
      operation: () => Promise<T>,
      customOptions?: UseErrorHandlerOptions
    ): Promise<T | null> => {
      try {
        return await operation();
      } catch (error) {
        const finalOptions = { ...options, ...customOptions };
        if (finalOptions.showNotification !== false) {
          handleError(error, finalOptions);
        }
        finalOptions.onError?.(error);
        return null;
      }
    },
    [options, handleError]
  );

  return {
    // Error handling methods
    handleError,
    handleSuccess,
    handleWarning,
    handleInfo,

    // Field error extraction
    getFieldError,

    // Custom error creation
    createError,

    // Async error handling
    withErrorHandling,

    // Error type checkers
    isNetworkError: ErrorService.isNetworkError,
    isAuthError: ErrorService.isAuthError,
    isValidationError: ErrorService.isValidationError,

    // Parse error
    parseError: ErrorService.parseError,
  };
}

export default useErrorHandler;
